import {
    CommonModule
} from '@angular/common';

import {
    Component,
    inject,
    Renderer2,
    signal
} from '@angular/core';

import {
    MatDialogModule
} from '@angular/material/dialog';

import {
    MatIconModule
} from '@angular/material/icon';

import {
    TranslateModule,
    TranslateService
} from '@ngx-translate/core';

import {
    CustomForm,
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
    KeyValueIconImage,
    TilbyMagicFormComponent
} from '@tilby/tilby-ui-lib/components/tilby-magic-form';

import {
    ConfigurationManagerService,
    KeyboardDimension,
    RightSidenavService,
    ThemeModeService
} from 'src/app/core';

import {
    CashregisterStateService,
    CashregisterTopbarSettingsFields
} from '../../services/cashregister.state.service';

import {
    distinctUntilChanged,
    skip,
} from 'rxjs';

import {
    MatButtonModule
} from '@angular/material/button';

import {
    CASHREGISTER_AVAILABILITY,
    CASHREGISTER_CATEGORIES,
    CASHREGISTER_ITEMS,
    CASHREGISTER_ORDER,
    CASHREGISTER_SEARCH_IN_ALL_CATEGORIES,
    CASHREGISTER_VIEW
} from 'src/app/core/constants/cashregister-settings';

import {
    KeyValue
} from '@angular/common';

import {
    ItemReorderDialogService
} from 'src/app/dialogs/cashregister/item-reorder-dialog';

import {
    OpenDialogsService
} from 'src/app/dialogs';
import { subscribeInComponent } from 'src/app/shared/angular-utils';

@Component({
    standalone: true,
    selector: 'app-cashregister-topbar-settings',
    templateUrl: './cashregister-topbar-settings.component.html',
    styleUrls: ['./cashregister-topbar-settings.component.scss'],
    imports: [
        CommonModule,
        MatIconModule,
        TranslateModule,
        TilbyMagicFormComponent,
        MatDialogModule,
        MatButtonModule
    ]
})
export class CashregisterTopbarSettingsComponent {

    cashregisterTopbarSettingsForm :CustomFormGroup<CustomForm<CashregisterTopbarSettingsFields>>;

    private readonly itemReorderDialogService = inject(ItemReorderDialogService);
    private readonly cashregisterStateService = inject(CashregisterStateService);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly translateService = inject(TranslateService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly renderer = inject(Renderer2);
    private readonly rightSidenavService = inject(RightSidenavService);
    private readonly themeModeService = inject(ThemeModeService);
    private readonly newUi = signal(this.configurationManagerService.isModuleAngular('cashregister.ui'));

    constructor() {
        const categoryView = this.configurationManagerService.getPreference("showcase_category_view") || 'tab'; // 'tab' | 'grid'
        const categoryOrderBy = this.configurationManagerService.getPreference("showcase_category_order_by") || '+index';
        const categoryGridRows = parseInt(this.configurationManagerService.getPreference("showcase_category_grid_rows") || "3", 10);
        const categoryGridColumns = parseInt(this.configurationManagerService.getPreference("showcase_category_grid_columns") || "-1", 10);
        const showcaseMode = this.configurationManagerService.getPreference("showcase_mode") || 'photoGrid'; //photoGrid noPhotoGrid list
        const itemIcon = this.configurationManagerService.getPreference("showcase_item_icon") || false;
        const itemGridColumns = parseInt(this.configurationManagerService.getPreference("showcase_item_grid_columns") || "-1", 10);
        const displayMode = this.configurationManagerService.getPreference("showcase_display_mode") || 'normal';
        const orderBy = this.configurationManagerService.getPreference("showcase_order_by") || '+name';
        const showUnavailable = this.configurationManagerService.getPreference("showcase_show_unavailable") === false ? false : true;
        const searchInAllCategories = !Boolean(this.configurationManagerService.getPreference("search_in_all_categories")) ? false : true;
        const showStockQuantity = !Boolean(this.configurationManagerService.getPreference("cashregister.show_stock_quantity_on_showcase")) ? false : true;
        const fontNameSize = this.cashregisterStateService.fontNameSizeSignal();
        const fontPriceSize = this.cashregisterStateService.fontPriceSizeSignal();
        const categoryFontNameSize = this.cashregisterStateService.categoryFontNameSizeSignal();
        const expandCartRows = this.cashregisterStateService.expandCartRowsSignal();
        const keyboardDimension = this.cashregisterStateService.keyboardDimensionSignal();
        const keyboardFontSize = this.cashregisterStateService.keyboardFontSizeSignal();

        const categoriesChoises: KeyValueIconImage<string>[] = CASHREGISTER_CATEGORIES.map((categories, _i) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.CATEGORIES.${categories.name.toUpperCase()}`,
            value: categories.id,
            image: `assets/images/cashregister/settings/categories/${this.getImageName(categories.imageUrl)}`
        }))

        const itemsChoises: KeyValueIconImage<string>[] = CASHREGISTER_ITEMS.map((items, _i) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.ITEMS.${items.name.toUpperCase()}`,
            value: items.id,
            image: `assets/images/cashregister/settings/items/${this.getImageName(items.imageUrl)}`
        }))

        const viewChoises: KeyValueIconImage<string>[] = CASHREGISTER_VIEW.map((view, _i) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.VIEW.${view.name.toUpperCase()}`,
            value: view.id,
            image: `assets/images/cashregister/settings/view/${this.getImageName(view.imageUrl)}`
        }))

        const orderChoises: KeyValueIconImage<string>[] = CASHREGISTER_ORDER.map((order, _i) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.ORDER.${order.name.toUpperCase()}`,
            value: order.id
        }))

        const categoryOrderChoises: KeyValueIconImage<string>[] = [
            { key: 'CASHREGISTER.TOPBAR.SETTINGS.ORDER.INDEX', value: '+index' },
            { key: 'CASHREGISTER.TOPBAR.SETTINGS.ORDER.NAME', value: '+name' }
        ];

        const availabilityChoises: KeyValueIconImage<string>[] = CASHREGISTER_AVAILABILITY.map((availability, _i) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.AVAILABILITY.${availability.name.toUpperCase()}`,
            value: availability.id,
        }))

        const searchCategoriesChoises: KeyValueIconImage<string>[] = CASHREGISTER_SEARCH_IN_ALL_CATEGORIES.map((searchCategories, _i) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.SEARCH_CATEGORIES.${searchCategories.name.toUpperCase()}`,
            value: searchCategories.id + '',
        }))

        const keyboardDimensionOptions: KeyValue<string, string>[] = ['small', 'medium', 'large'].map((val) => ({
            key: `CASHREGISTER.TOPBAR.SETTINGS.KEYBOARD_DIMENSIONS.${val.toUpperCase()}`,
            value: val,
        }));

        const cartRowsChoises: KeyValueIconImage<string>[] = [
            { key: 'CASHREGISTER.TOPBAR.SETTINGS.CART.COLLAPSED', value: 'false' },
            { key: 'CASHREGISTER.TOPBAR.SETTINGS.CART.EXPANDED', value: 'true' }
        ];

        const categoryGridRowsOptions: KeyValue<string, number>[] = [
            ...Array.from({ length: 6 }, (_, i) => ({
                key: `${i + 1}`,
                value: i + 1
            }))
        ];

        const categoryGridColumnsOptions: KeyValue<string, number>[] = [
            {
                key: 'CASHREGISTER.TOPBAR.SETTINGS.CATEGORIES.COLUMNS_SELECTED_DEFAULT',
                value: -1,
            },
            ...Array.from({ length: 4 }, (_, i) => ({
                key: `${i + 3}`,
                value: i + 3
            }))
        ];

        const itemGridColumnsOptions: KeyValue<string, number>[] = [
            {
                key: 'CASHREGISTER.TOPBAR.SETTINGS.ITEMS.COLUMNS_SELECTED_DEFAULT',
                value: -1,
            },
            ...Array.from({ length: 4 }, (_, i) => ({
                key: `${i + 3}`,
                value: i + 3
            }))
        ];

        const fontSizeValues: KeyValue<string, number>[] = [
            ...Array.from({ length: 9 }, (_, i) => ({
                key: `${i + 12}`,
                value: i + 12
            }))
        ];

        const keyboardFontSizeValues: KeyValue<string, number>[] = [
            ...Array.from({ length: 19 }, (_, i) => ({
                key: `${i + 12}`,
                value: i + 12
            }))
        ];

        const form = new CustomFormGroup<CustomForm<CashregisterTopbarSettingsFields>>({
            categories: new CustomFormGroup<CustomForm<{category: string, category_grid_rows: number, category_grid_columns?: number, category_font_name_size: number, category_order_by: string}>>({
                category: new CustomFormControl(categoryView, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.CATEGORIES.TITLE',
                    inputType: 'radio',
                    class: `magic-radio-list magic-radio-list__with-image tw-m-0`,
                    inputChoices: categoriesChoises
                }),
                category_order_by: new CustomFormControl(categoryOrderBy, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.ORDER.TITLE',
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: categoryOrderChoises,
                }),
                category_grid_rows: new CustomFormControl(categoryGridRows, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.CATEGORIES.ROWS_SELECTED',
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: categoryGridRowsOptions
                }),
                category_grid_columns: new CustomFormControl(categoryGridColumns, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.CATEGORIES.COLUMNS_SELECTED',
                    inputType: this.newUi() ? 'hidden' : 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: categoryGridColumnsOptions
                }),
                category_font_name_size: new CustomFormControl(categoryFontNameSize, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.CATEGORIES.FONT_NAME_SIZE',
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: fontSizeValues
                })
            }),
            items: new CustomFormGroup<CustomForm<{item: string, item_icon: boolean, item_grid_columns: number, font_name_size: number, font_price_size: number}>>({
                item: new CustomFormControl(showcaseMode, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.ITEMS.TITLE',
                    inputType: 'radio',
                    class: 'magic-radio-list magic-radio-list__with-image tw-border-0 tw-border-t tw-border-solid tw-border-gray-300 tw-m-0',
                    inputChoices: itemsChoises
                }),
                item_icon: new CustomFormControl(itemIcon, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.ITEMS.PRODUCT_ICON',
                    inputType: 'checkbox',
                    class: 'magic-radio-list magic-radio-list__with-image tw-m-0',
                }),
                item_grid_columns: new CustomFormControl(itemGridColumns, {}, {
                    ...new CustomFormControlProps(),
                    label: `CASHREGISTER.TOPBAR.SETTINGS.ITEMS.COLUMNS_SELECTED`,
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: itemGridColumnsOptions
                }),
                font_name_size: new CustomFormControl(fontNameSize, {}, {
                    ...new CustomFormControlProps(),
                    label: `CASHREGISTER.TOPBAR.SETTINGS.ITEMS.FONT_NAME_SIZE`,
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: fontSizeValues
                }),
                font_price_size: new CustomFormControl(fontPriceSize, {}, {
                    ...new CustomFormControlProps(),
                    label: `CASHREGISTER.TOPBAR.SETTINGS.ITEMS.FONT_PRICE_SIZE`,
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: fontSizeValues
                })
            }),
            view: new CustomFormGroup<CustomForm<{view: string}>>({
                view: new CustomFormControl(displayMode, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.VIEW.TITLE',
                    inputType: 'radio',
                    class: 'magic-radio-list magic-radio-list__with-image tw-border-0 tw-border-b tw-border-t tw-border-solid tw-border-gray-300 tw-m-0',
                    inputChoices: viewChoises
                })
            }),
            order: new CustomFormGroup<CustomForm<{order: string}>>({
                order: new CustomFormControl(orderBy, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.ORDER.TITLE',
                    inputType: 'radio',
                    class: 'magic-radio-list tw-border-0 tw-border-b tw-border-solid tw-border-gray-300 tw-m-0',
                    inputChoices: orderChoises,
                    id: 'item-order-id'
                })
            }),
            availability: new CustomFormGroup<CustomForm<{availability: string}>>({
                availability: new CustomFormControl(String(!!showUnavailable), {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.AVAILABILITY.TITLE',
                    inputType: 'radio',
                    class: 'magic-radio-list tw-border-0 tw-border-b tw-border-solid tw-border-gray-300 tw-m-0 ',
                    inputChoices: availabilityChoises
                })
            }),
            search_in_all_category: new CustomFormGroup<CustomForm<{searchCategory: string}>>({
                searchCategory: new CustomFormControl(String(!!searchInAllCategories), {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.SEARCH_CATEGORIES.TITLE',
                    inputType: 'radio',
                    class: 'magic-radio-list tw-border-0 tw-border-b tw-border-solid tw-border-gray-300 tw-m-0 ',
                    inputChoices: searchCategoriesChoises
                })
            }),
            show_stock_level: new CustomFormGroup<CustomForm<{showStock: boolean}>>({
                showStock: new CustomFormControl(!!showStockQuantity, {}, {
                        ...new CustomFormControlProps(),
                        hint: () => ``,
                        label: 'CASHREGISTER.TOPBAR.SETTINGS.SHOW_STOCK_LEVEL.TITLE',
                        inputType: 'checkbox',
                        class: 'tw-w-full tw-mb-2 tw-pl-4 tw-pt-3 tw-text-[12px]'
                })
            }),
            keyboard: new CustomFormGroup<CustomForm<{keyboard_dimension: KeyboardDimension, keyboard_font_size: number}>>({
                keyboard_dimension: new CustomFormControl(keyboardDimension, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.KEYBOARD_DIMENSIONS.TITLE',
                    inputType: 'radio',
                    class: 'magic-radio-list tw-m-0',
                    inputChoices: keyboardDimensionOptions
                }),
                keyboard_font_size: new CustomFormControl(keyboardFontSize, {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.KEYBOARD.FONT_SIZE_KEYS',
                    inputType: 'select',
                    class: 'tw-max-w-[12rem] p-[5px] tw-px-3',
                    inputChoices: keyboardFontSizeValues
                })
            }),
            cart: new CustomFormGroup<CustomForm<{expand_rows: string}>>({
                expand_rows: new CustomFormControl(String(expandCartRows), {}, {
                    ...new CustomFormControlProps(),
                    label: 'CASHREGISTER.TOPBAR.SETTINGS.CART.TITLE',
                    inputType: this.newUi() ? 'radio' : 'hidden',
                    class: 'magic-radio-list tw-border-0 tw-border-t tw-border-solid tw-border-gray-300 tw-m-0',
                    inputChoices: cartRowsChoises
                })
            })
        });

        const changesMobilePortrait = () => {
            if(!this.cashregisterStateService.isMobilePortrait()) {
                return;
            }

            if(form.controls.categories.controls.category) {
                form.controls.categories.controls.category.customProps.inputType = 'hidden';
            }

            if(form.controls.items.controls.item) {
                form.controls.items.controls.item.customProps.inputType = 'hidden';
            }

            if(form.controls.view.controls.view) {
                form.controls.view.controls.view.customProps.inputType = 'hidden';
            }
        }

        const changesFunction = (value : string) => {
            if(form.controls.categories.controls.category_grid_rows) {
                form.controls.categories.controls.category_grid_rows.customProps.inputType = value === 'grid' ? 'select' : 'hidden';
            }
        }

        const changesItemsGridColumns = (value : string) => {
            switch (value) {
                case 'photoGrid':
                case 'noPhotoGrid':
                case 'tab':
                    form.controls.items.controls.item_grid_columns.customProps.inputType = 'select';
                break;
                default:
                    form.controls.items.controls.item_grid_columns.customProps.inputType = 'hidden';
                break;
            }
        }

        const changesItemIcon = (value: boolean) => {
            this.cashregisterStateService.showItemIconSignal.set(value);
            this.configurationManagerService.setUserPreference('showcase_item_icon', value);
        }

        const changesCategoryFontNameSize = (value: number) => {
            this.configurationManagerService.setUserPreference('cashregister.category_font_name_size', value);
            this.cashregisterStateService.categoryFontNameSizeSignal.set(value);
        }

        const changesCategoryOrderBy = (value: any) => {
            this.configurationManagerService.setUserPreference('showcase_category_order_by', value);
            this.cashregisterStateService.eventsSettings.next({ category_order_by: value });
        }

        const changesFontNameSize = (value: number) => {
            this.configurationManagerService.setUserPreference('cashregister.font_name_size', value);
            this.cashregisterStateService.fontNameSizeSignal.set(value);
        }

        const changesFontPriceSize = (value: number) => {
            this.configurationManagerService.setUserPreference('cashregister.font_price_size', value)
            this.cashregisterStateService.fontPriceSizeSignal.set(value);
        }

        const changesExpandCartRows = (value: boolean) => {
            this.configurationManagerService.setUserPreference('cashregister.expand_cart_rows', !!value);
            this.cashregisterStateService.expandCartRowsSignal.set(!!value);
        }

        const changesKeyboardDimension = (value : KeyboardDimension) => {
            this.configurationManagerService.setUserPreference('cashregister.keyboard_dimension', value);
            this.cashregisterStateService.keyboardDimensionSignal.set(value);
        }

        const changesKeyboardFontSize = (value : number) => {
            this.configurationManagerService.setUserPreference('cashregister.keyboard_font_size', value);
            this.cashregisterStateService.keyboardFontSizeSignal.set(value);
        }

        subscribeInComponent(form.controls.categories.controls.category.valueChanges.pipe(distinctUntilChanged()), (value: string) => {
            changesFunction(value);
        });

        subscribeInComponent(form.controls.items.controls.item.valueChanges.pipe(distinctUntilChanged()), (value: string) => {
            changesItemsGridColumns(value);
        });

        subscribeInComponent(form.controls.items.controls.item_icon.valueChanges.pipe(distinctUntilChanged()), (value: boolean) => {
            changesItemIcon(value);
        });

        subscribeInComponent(form.controls.items.controls.font_name_size.valueChanges.pipe(distinctUntilChanged()), (value: number) => {
            changesFontNameSize(value);
        });

        subscribeInComponent(form.controls.categories.controls.category_font_name_size.valueChanges.pipe(distinctUntilChanged()), (value: number) => {
            changesCategoryFontNameSize(value);
        });

        subscribeInComponent(form.controls.categories.controls.category_order_by.valueChanges.pipe(distinctUntilChanged()), (value: any) => {
            changesCategoryOrderBy(value);
        });

        subscribeInComponent(form.controls.items.controls.font_price_size.valueChanges.pipe(distinctUntilChanged()), (value: number) => {
            changesFontPriceSize(value);
        });

        subscribeInComponent(form.controls.cart.controls.expand_rows.valueChanges.pipe(distinctUntilChanged()), (value: string) => {
            changesExpandCartRows(value === 'true');
        });

        subscribeInComponent(form.controls.keyboard.controls.keyboard_dimension.valueChanges.pipe(distinctUntilChanged()), (value: KeyboardDimension) => {
            changesKeyboardDimension(value);
        });

        subscribeInComponent(form.controls.keyboard.controls.keyboard_font_size.valueChanges.pipe(distinctUntilChanged()), (value: number) => {
            changesKeyboardFontSize(value);
        });

        subscribeInComponent(form.controls.categories.controls.category.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ category: value });
        });

        subscribeInComponent(form.controls.categories.controls.category_grid_rows.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ category_grid_rows: value });
        });

        const categoryGridColumnsControl = form.controls.categories.controls.category_grid_columns;
        if (categoryGridColumnsControl) {
            subscribeInComponent(categoryGridColumnsControl.valueChanges.pipe(skip(1)), (value) => {
                this.cashregisterStateService.eventsSettings.next({ category_grid_columns: value });
            });
        }

        subscribeInComponent(form.controls.items.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ item: value.item });
        });

        subscribeInComponent(form.controls.items.controls.item_grid_columns.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ item_grid_columns: value });
        });

        subscribeInComponent(form.controls.view.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ view: value.view });
        });

        subscribeInComponent(form.controls.order.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ order: value.order });
        });

        subscribeInComponent(form.controls.availability.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ availability: value.availability === "true" });
        });

        subscribeInComponent(form.controls.search_in_all_category.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ search_in_all_categories: value.searchCategory === "true" });
        });

        subscribeInComponent(form.controls.show_stock_level.valueChanges.pipe(skip(1)), (value) => {
            this.cashregisterStateService.eventsSettings.next({ show_stock_quantity: !!value.showStock });
        });

        changesMobilePortrait();
        changesFunction(categoryView);
        changesCategoryOrderBy(categoryOrderBy);
        changesItemsGridColumns(showcaseMode);
        changesItemIcon(itemIcon);
        changesCategoryFontNameSize(categoryFontNameSize);
        changesFontNameSize(fontNameSize);
        changesFontPriceSize(fontPriceSize);
        changesExpandCartRows(expandCartRows);
        changesKeyboardDimension(keyboardDimension);
        changesKeyboardFontSize(keyboardFontSize);
    
        this.cashregisterTopbarSettingsForm = form;
    }

    private setReorderButtonStatus(buttonReorder: any, value: string) {
        if (value == "+index") {
            this.renderer.removeAttribute(buttonReorder, 'disabled');
        } else {
            this.renderer.setAttribute(buttonReorder, 'disabled', 'false');
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            const order = this.cashregisterTopbarSettingsForm.controls.order;
            this.createButtonReorder();

            const divReorder = this.renderer.selectRootElement('#div-reorder', true);
            const buttonReorder = divReorder.querySelectorAll('button')[0];

            const initialOrderValue = order.getRawValue().order;

            this.setReorderButtonStatus(buttonReorder, initialOrderValue);

            order.valueChanges.subscribe((value) => this.setReorderButtonStatus(buttonReorder, value.order));
        }, 1);
    }

    createButtonReorder() {
        const elementA = this.renderer.selectRootElement('#item-order-id', true);
        const radioButtons = elementA.querySelectorAll('mat-radio-button');
        const secondRadioButton = radioButtons[1];
        const div1 = secondRadioButton.querySelectorAll('div');
        const div = this.renderer.createElement('div');
        this.renderer.setAttribute(div, 'id', 'div-reorder');
        const button = this.renderer.createElement('button');
        const buttonText = this.renderer.createText(this.translateService.instant('CASHREGISTER.TOPBAR.SETTINGS.REORDER.BTN_REORDER'));
        this.renderer.appendChild(button, buttonText);
        this.renderer.addClass(button, 'mat-stroked-button');
        this.renderer.listen(button, 'click', () => {
            this.callDialog().then(() => {
            }).catch((error) => {
              console.error('Errore durante la chiamata :', error);
            });
          });
        this.renderer.appendChild(div, button);
        this.renderer.appendChild(div1[0], div);
    }

    private getImageName(name: string) {
        return this.themeModeService.isDarkTheme() ? name.replace(".svg", "-dark.svg") : name;
    }

    protected async callDialog() {
        const res = await this.itemReorderDialogService.openDialog();

        if (!res || !res.data.result) {
            return
        }

        if(res.data.result.success) {
            this.openDialogsService.openSnackBarTilby('CASHREGISTER.TOPBAR.SETTINGS.REORDER.REORDER_OK', 'MISC.OK', { duration: 3000 });
            return;
        }

        this.openDialogsService.openSnackBarTilby('CASHREGISTER.TOPBAR.SETTINGS.REORDER.REORDER_KO', 'MISC.OK', { duration: 3000 });
    }

    closeSidenav() {
        this.rightSidenavService.isOpen = false;
    }
}
